import Spinner from "@components/ui/spinner";
import { MediaLibFileCheckbox } from "@editor/modules/media-lib/components/files/file";
import { cn } from "@lib/cn";
import { type ReactNode, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";
import { LOGO_FINDER_CELL_HEIGHT } from "../contants";
import { useMediaLibLogoFinderCtx } from "../ctx";
import type { IMediaLibLogo } from "../types";

interface IProps {
  logoData: IMediaLibLogo;
  idx: number;
}

export const LogoFinderLogo = ({ idx, logoData }: IProps) => {
  const { updateLogoData, isMultiSelect, onSelect, selectedItems } =
    useMediaLibLogoFinderCtx();
  const [isEditingImage, setIsEditingImage] = useDebounceValue(false, 200);

  const handleChangeColor = async (color: IMediaLibLogo["color"]) => {
    if (!logoData?.src) return;

    if (color === "color") {
      updateLogoData(idx, { src: logoData.originalSrc, color });
      return;
    }

    setIsEditingImage(true);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = logoData.src;

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.filter = `brightness(0) ${color === "white" ? "invert()" : ""}`;

      ctx.drawImage(img, 0, 0);
      updateLogoData(idx, { src: canvas.toDataURL(), color });
      setIsEditingImage(false);
    };
    img.onerror = () => {
      setIsEditingImage(false);
    };
  };

  let className = "";
  let content: ReactNode = null;

  className = cn(
    "tw-relative tw-rounded-lg tw-border tw-border-blue-base/10 hover:tw-border-primary tw-transition-colors tw-cursor-pointer",
    logoData.color === "white"
      ? "tw-bg-blue-900"
      : "tw-bg-blue-50 hover:tw-bg-primary-50 ",
  );
  const isAddingDisabled = isEditingImage || !logoData?.src;
  content = (
    <div className="tw-size-full">
      <div className="tw-size-full" draggable={!isAddingDisabled}>
        <img
          draggable={false}
          key={logoData.src}
          src={logoData.src}
          loading="lazy"
          alt={logoData.alt}
          className="tw-pointer-events-none tw-size-full tw-object-contain tw-p-6"
        />
      </div>
    </div>
  );

  const isSelected = useMemo(() => {
    if (!selectedItems) return false;
    return !!selectedItems.find((el) => el.id === logoData.src);
  }, [selectedItems, logoData.src]);

  const shouldShowColors =
    !!content &&
    !logoData?.src.endsWith(".svg") &&
    (logoData?.type === "logo" || logoData?.type === "symbol");

  return (
    <div
      onClick={() => onSelect?.(logoData)}
      className={cn(className, "tw-group tw-relative")}
      style={{
        height: LOGO_FINDER_CELL_HEIGHT,
      }}
    >
      {isMultiSelect && <MediaLibFileCheckbox isSelected={isSelected} />}
      {isEditingImage ? (
        <div className="tw-absolute tw-left-1 tw-top-1">
          <Spinner />
        </div>
      ) : (
        shouldShowColors && (
          <div className="tw-absolute tw-right-1 tw-top-1 tw-flex tw-space-x-2 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
            <button
              type="button"
              className="tw-size-4 tw-cursor-pointer tw-rounded-full  tw-outline-none tw-transition-opacity hover:tw-opacity-80"
              style={{
                background:
                  "conic-gradient(#ffe278 45deg, #ffb54c 45deg 90deg, #ff776b 90deg 135deg, #f71e78 135deg 180deg, #1265c2 180deg 225deg, #08a8ed 225deg 270deg, #45c5ff 270deg 315deg, #b0d16f 315deg 360deg)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleChangeColor("color");
              }}
            />
            <button
              type="button"
              className="tw-size-4 tw-cursor-pointer tw-rounded-full  tw-bg-black tw-outline-none tw-transition-opacity hover:tw-opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                handleChangeColor("black");
              }}
            />

            <button
              type="button"
              className="tw-size-4 tw-cursor-pointer tw-rounded-full  tw-border tw-border-blue-200 tw-bg-white tw-outline-none tw-transition-opacity hover:tw-opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                handleChangeColor("white");
              }}
            />
          </div>
        )
      )}

      {content}
    </div>
  );
};
